import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Divider, Drawer, Typography, Checkbox, Stack, IconButton } from "@mui/material";
import { grey } from "@mui/material/colors";
import { X } from "lucide-react";
import { RootState } from "@/store/store";
import { addTips, removeTips } from "./slice";
import { useAppDispatch, useAppSelector, useResponsive } from "@/hooks";
import OrderItemsList from "./components/OrderItemsList";
import AppConfig from "@/components/AppConfig";
import { EmptyState } from "@/components";
import Price from "@/components/Price";
import EmptyIllustration from "./assets/empty.png";
import { ItemStatus, OrderItem } from "@/types";
import { Configs } from "@/constants/configs";
import useRoutes from "@/hooks/useRoutes";
import { getOrderSubtotal, getOrderTaxes, getOrderTotal } from "@/utils/order";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const Order = ({ isOpen, onClose }: Props) => {
  const [navigate] = useRoutes();
  const dispatch = useAppDispatch();
  const { organizationId } = useParams();
  const order = useAppSelector((state: RootState) => state.order);
  const flowParam = new URLSearchParams(document.location.search).get("flow");
  const organization = useAppSelector((state) => state.organizations[organizationId]);
  const { isMobile } = useResponsive();

  const toggleTips = () => {
    dispatch(order.areTipsIncluded ? removeTips() : addTips());
  };

  const showTaxes = useMemo(() => {
    return Boolean(organization?.settings?.includeTax);
  }, [organization?.settings?.includeTax]);

  const tips = useMemo(() => {
    if (flowParam === "dine-in") {
      return order?.areTipsIncluded ? getOrderSubtotal(order.items as OrderItem[]) * (order?.tipPercentage / 100) : 0;
    }
    return order?.areTipsIncluded ? 0 : 0;
  }, [flowParam, order?.areTipsIncluded, order.items, order?.tipPercentage]);

  const areAllItemsDelivered = useMemo(() => {
    return !(order?.items ?? []).find((item: OrderItem) => item?.status !== ItemStatus.DELIVERED);
  }, [order?.items]);

  return (
    <Drawer
      sx={{ border: 1 }}
      anchor={isMobile ? "top" : "right"}
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderBottomLeftRadius: isMobile ? "32px" : 0,
          borderBottomRightRadius: isMobile ? "32px" : 0,
          width: isMobile ? "100%" : "50%",
        },
      }}
    >
      <Box>
        <Stack direction={"row"} spacing={2} alignItems="center" justifyContent="space-between" px={3} py={1}>
          <Typography variant="h2">Cuenta</Typography>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </Stack>
        <Divider color={grey[300]} />

        {!order.items?.length ? (
          <Stack p={4} alignItems="center">
            <img src={EmptyIllustration} width={300} alt="Cuenta vacía" />
            <EmptyState
              title="Tu cuenta está vacía"
              description="Cuando agregues algo a tu cuenta aparecerá justo aquí"
              action={{
                children: "Agregar",
                onClick: onClose,
              }}
            />
          </Stack>
        ) : null}

        <Stack>
          <OrderItemsList items={order.items} />
          <Divider color={grey[300]} />
          <Stack spacing={1} px={4} py={3}>
            {/* SUBTOTAL */}
            {showTaxes ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" fontSize={"18px"}>
                  Subtotal
                </Typography>
                <Typography variant="h4" fontSize={"18px"}>
                  <Price currency="NIO">{getOrderSubtotal(order.items)}</Price>
                </Typography>
              </Stack>
            ) : null}

            {/* TAXES */}
            {showTaxes ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" fontSize={"18px"}>
                  IVA
                </Typography>
                <Typography variant="h4" fontSize={"18px"}>
                  <Price currency="NIO">{getOrderTaxes(order.items)}</Price>
                </Typography>
              </Stack>
            ) : null}

            {/* TIPS */}
            {flowParam === "dine-in" ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Propina {order.tipPercentage}% (voluntaria)</Typography>
                  <Checkbox checked={order.areTipsIncluded} value={!order.areTipsIncluded} onChange={toggleTips} sx={{ padding: 0 }} />
                </Stack>
                <Typography>
                  <Price currency="NIO">{tips}</Price>
                </Typography>
              </Stack>
            ) : null}

            {/* TOTAL */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h3" fontWeight="bold">
                Total
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                <Price currency="NIO">{showTaxes ? getOrderTotal(order.items as OrderItem[]) + tips : getOrderSubtotal(order.items as OrderItem[])}</Price>
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {flowParam?.includes("delivery") ? (
          <>
            <Divider />
            {order.items?.length ? (
              <Stack px={"24px"} py={"16px"} spacing={2}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => navigate("organizations.order.checkout", { orderId: order.id })}
                  sx={{
                    fontWeight: "regular",
                    fontSize: "18px",
                  }}
                >
                  Continuar
                </Button>
              </Stack>
            ) : null}
          </>
        ) : (
          <AppConfig isEnabled={Configs.ADD_ORDER}>
            <Divider />
            <Stack px={"24px"} py={"16px"} spacing={2}>
              <Button variant="contained" onClick={() => alert("Test")} disabled={!areAllItemsDelivered}>
                Pagar
              </Button>
            </Stack>
          </AppConfig>
        )}
      </Box>
    </Drawer>
  );
};

export default Order;
